import React from 'react'
import { useNavigate } from 'react-router-dom'
import { COLORS } from '../constants'
import Select, { components } from 'react-select'
import { Modal, ModalBody, Button } from 'reactstrap'
import { getCountryFlag } from '../utils'

// Country flag component with styling
export const CountryFlag = ({ countryName, style = {} }) => {
  // Extract country name from "Country (Language)" format if needed
  const actualCountry = countryName.includes("(")
    ? countryName.split("(")[0].trim()
    : countryName;

  const FlagComponent = getCountryFlag(actualCountry);

  if (!FlagComponent) return null;

  return (
    <FlagComponent
      style={{
        width: '20px',
        height: 'auto',
        marginRight: '6px',
        verticalAlign: 'middle',
        ...style
      }}
    />
  );
};

// react-select Option for countries (with flag)
const CountryOption = (props) => {
  const { innerRef, innerProps, data, isFocused, isSelected } = props
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '6px 12px',
        background: isFocused ? '#3A3B47' : 'transparent',
        color: isSelected ? COLORS.primary : COLORS.white,
        cursor: 'pointer',
      }}
    >
      <CountryFlag countryName={data.label} />
      {data.label}
    </div>
  )
}

const SingleValue = (props) => (
  <components.SingleValue {...props}>
    <CountryFlag countryName={props.data.label} />
    {props.data.label}
  </components.SingleValue>
)

const FilterControls = ({
  countriesData,
  selectedCountry,
  selectedLanguage,
  availableLanguages,
  selectionError,
  onCountryChange,
  onCountrySelect,
  onLanguageChange,
  trackedCountries = [],
  activeTrackedCountry = "",
  onTrackedCountryClick = () => { },
  trackedCountryLanguagePairs = [], // New prop for country-language pairs
  activeTrackedPair = "", // New prop for active pair
  onTrackedPairClick = null, // New handler for pair clicks
  onRemoveTrackedPair = () => { },
  hasKeywordsForPair = null,
  trackedApp = null
}) => {
  // Use the appropriate click handler
  const handlePairClick = onTrackedPairClick || onTrackedCountryClick;

  const navigate = useNavigate()

  // const handleGoToDashboard = () => {
  //   if (trackedApp?.item_id) {
  //     navigate(`/xr/app/${(trackedApp.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${trackedApp.item_id}`)
  //   }
  // }

  // Local buffered selections: user chooses country & language locally
  // and only when they click "Add" do we notify the parent.
  const [localCountry, setLocalCountry] = React.useState(selectedCountry || "")
  const [localLanguage, setLocalLanguage] = React.useState(selectedLanguage || "")
  const [pairToRemove, setPairToRemove] = React.useState(null)

  // Keep local values in sync when parent changes selected props
  React.useEffect(() => {
    setLocalCountry(selectedCountry || "")
  }, [selectedCountry])

  React.useEffect(() => {
    setLocalLanguage(selectedLanguage || "")
  }, [selectedLanguage])
  // (selection UI is below)

  // Generate display pairs without duplicates
  const getDisplayPairs = () => {
    let displayPairs = [];

    // If we have tracked country-language pairs, use them
    if (trackedCountryLanguagePairs.length > 0) {
      displayPairs = [...trackedCountryLanguagePairs];
    } else if (trackedCountries.length > 0) {
      // Convert tracked countries to pairs
      displayPairs = trackedCountries.map(country =>
        selectedCountry === country && selectedLanguage
          ? `${country} (${selectedLanguage})`
          : country
      );
    }

    // Add current selection if it's not already in the list
    // REMOVED: We only want to show pairs that are explicitly tracked (have keywords)
    // or are in the trackedCountryLanguagePairs list.
    // The user must click "Add" to create a new tracked pair.

    // Remove duplicates and empty entries
    let result = [...new Set(displayPairs)].filter(pair => pair && pair.trim() !== '');

    // Filter out pairs that have no keywords (using hasKeywordsForPair if provided)
    if (typeof hasKeywordsForPair === 'function') {
      result = result.filter(pair => hasKeywordsForPair(pair));
    }

    return result;
  };

  const displayPairs = getDisplayPairs();

  return (
    <div className="track_data_sec">
      {/* Country & Language Selection Section */}
      <div
        className="track_country_main"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "4px",
          marginBottom: "20px",
        }}
      >
        <label style={{ color: COLORS.white }}>
          Select Country & Language
          {!selectedCountry || !selectedLanguage ? (
            <span style={{
              color: "#94a3b8",
              marginLeft: "8px",
              fontSize: "12px",
              fontWeight: "normal"
            }}>
              Required to add keywords
            </span>
          ) : (
            <span style={{
              color: "#4ade80",
              marginLeft: "8px",
              fontSize: "12px",
              fontWeight: "normal"
            }}>
              Ready to add keywords
            </span>
          )}
        </label>

        <div
          className="combined-select"
          style={{
            display: "flex",
            alignItems: "center",
            background: !selectedCountry || !selectedLanguage ? "#2A2B37" : "#1a1b23",
            border: !selectedCountry || !selectedLanguage ? "2px solid #f43f5e" : "1px solid #3A3B47",
            borderRadius: "6px",
            padding: "4px 6px",
            gap: "6px",
            transition: "all 0.3s ease",
            position: "relative"
          }}
        >
          {/* First Row - Country */}
          <div style={{ position: 'relative', width: '100%' }}>
            <Select
              options={countriesData.map(c => ({ value: c.name, label: c.name }))}
              value={localCountry ? { value: localCountry, label: localCountry } : null}
              onChange={(option) => {
                const val = option?.value || ''
                setLocalCountry(val)
                // Call both handlers to ensure state is updated
                if (typeof onCountrySelect === 'function') {
                  onCountrySelect(val)
                }
                if (typeof onCountryChange === 'function') {
                  onCountryChange({ target: { value: val } })
                }
              }}
              placeholder="Select Country"
              components={{ Option: CountryOption, SingleValue }}
              styles={{
                control: (p) => ({ ...p, background: 'transparent', border: 'none', boxShadow: 'none', minHeight: '36px' }),
                singleValue: (p) => ({
                  ...p,
                  color: COLORS.white,
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '12px',
                  lineHeight: '18px'
                }),
                menu: (p) => ({ ...p, background: '#2A2B37', color: COLORS.white }),
                option: (p, s) => ({ ...p, background: s.isFocused ? '#3A3B47' : 'transparent', color: s.isSelected ? COLORS.primary : COLORS.white }),
                dropdownIndicator: (p) => ({ ...p, color: '#aaa' }),
                indicatorSeparator: (p) => ({ ...p, backgroundColor: '#3A3B47' }),
              }}
            />
          </div>
        </div>

        {/* Second Row - Language */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            background: !selectedCountry || !selectedLanguage ? "#2A2B37" : "#1a1b23",
            border: !selectedCountry || !selectedLanguage ? "2px solid #f43f5e" : "1px solid #3A3B47",
            borderRadius: "6px",
            padding: "4px 6px",
            gap: "6px",
            transition: "all 0.3s ease",
            position: "relative",
            marginTop: "8px"
          }}
        >
          <div style={{ position: 'relative', width: '100%' }}>
            <Select
              options={availableLanguages.map(l => ({ value: l, label: l }))}
              value={localLanguage ? { value: localLanguage, label: localLanguage } : null}
              onChange={(option) => {
                const val = option?.value || '';
                setLocalLanguage(val);
                if (typeof onLanguageChange === 'function') {
                  onLanguageChange({ target: { value: val } });
                }
              }}
              placeholder="Select Language"
              styles={{
                control: (p) => ({ ...p, background: 'transparent', border: 'none', boxShadow: 'none', minHeight: '36px' }),
                singleValue: (p) => ({
                  ...p,
                  color: COLORS.white,
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }),                
                menu: (p) => ({ ...p, background: '#2A2B37', color: COLORS.white }),
                option: (p, s) => ({ ...p, background: s.isFocused ? '#3A3B47' : 'transparent', color: s.isSelected ? COLORS.primary : COLORS.white }),
                dropdownIndicator: (p) => ({ ...p, color: '#aaa' }),
                indicatorSeparator: (p) => ({ ...p, backgroundColor: '#3A3B47' }),
              }}
            />
          </div>
        </div>
      </div>

      {selectionError && (
        <div style={{ color: COLORS.error, marginTop: "8px" }}>
          Please select both Country and Language before selecting a keyword.
        </div>
      )}

      {/* Remove confirmation modal */}
      <Modal isOpen={!!pairToRemove} toggle={() => setPairToRemove(null)} centered>
        <div className="modal-header">
          <h5 className="modal-title">Confirm Removal</h5>
          <button type="button" className="btn-close" onClick={() => setPairToRemove(null)} aria-label="Close" />
        </div>
        <ModalBody style={{ textAlign: 'center', padding: '20px' }}>
          {hasKeywordsForPair && hasKeywordsForPair(pairToRemove) ? (
            <p>
              The pair "<b>{pairToRemove}</b>" has tracked keywords. Removing it will also delete those keywords.
              This action cannot be undone. Do you want to proceed?
            </p>
          ) : (
            <p>Are you sure you want to remove "<b>{pairToRemove}</b>"? Data for this pair will be removed.</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '12px' }}>
            <Button color="danger" onClick={() => {
              if (pairToRemove && typeof onRemoveTrackedPair === 'function') {
                onRemoveTrackedPair(pairToRemove, { deleteKeywords: true })
              }
              setPairToRemove(null)
            }}>Yes</Button>
            <Button color="secondary" onClick={() => setPairToRemove(null)}>Cancel</Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Add CSS for styling options with flags */}
      <style jsx>{`
        .country-option {
          display: flex;
          align-items: center;
        }
      `}</style>
    </div>
  )
}

export default FilterControls